import React, { ReactNode, PropsWithChildren } from 'react'
import { CSSProperties } from 'styled-components'
import { isNil } from 'lodash-es'
import { Link } from 'react-router-dom'

import Loader from '@/ui/Loader'
import Popup from '@/ui/Popup'
import { ReactComponent as AddIcon } from '@/assets/images/icons/general/plus.svg'
import { ReactComponent as EditIcon } from '@/assets/images/icons/edit-03.svg'
import { ReactComponent as RemoveIcon } from '@/assets/images/icons/general/minus-circle.svg'
import {
  ButtonWrapper,
  StyledButton,
  StyledButtonGroup,
  NumberBubble,
} from './Styles'

type ButtonProps = {
  variant?:
    | 'primary'
    | 'outlined'
    | 'outlined-brand'
    | 'light-brand'
    | 'transparent'
  content?: ReactNode
  onClick?: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void
  leftIcon?: ReactNode
  rightIcon?: ReactNode
  iconColor?: string
  fluid?: boolean
  type?: 'button' | 'submit' | 'reset'
  count?: number
  countStyle?: 'inside' | 'notify'
  disabled?: boolean
  disabledReason?: string
  disabledReasonPopupPosition?:
    | 'top left'
    | 'top right'
    | 'bottom right'
    | 'bottom left'
    | 'right center'
    | 'left center'
    | 'top center'
    | 'bottom center'
  size?: 'small' | 'regular' | 'large' | 'huge'
  loading?: boolean
  linkTo?: string
  linkState?: any
  className?: string
  form?: string
  style?: CSSProperties
}

type ButtonGroupProps = {
  packTightly?: boolean
  vertical?: boolean
  className?: string
  style?: CSSProperties
}

const Button = ({
  variant = 'primary',
  content,
  onClick,
  leftIcon,
  rightIcon,
  iconColor,
  fluid = false,
  type,
  count,
  countStyle = 'inside',
  disabled = false,
  disabledReason,
  disabledReasonPopupPosition,
  size = 'regular',
  loading = false,
  linkTo,
  linkState,
  className,
  form,
  style,
  ...rest
}: ButtonProps) => {
  const classNames: string[] = ['button', variant]
  const wrapperClassNames: string[] = []

  if (className) {
    classNames.push(className)
  }

  if (fluid) {
    classNames.push('fluid')
    wrapperClassNames.push('fluid')
  }

  if (loading) {
    classNames.push('loading')
  }

  if (isNil(content) || content === '') {
    classNames.push('no-content')
  }

  const getSizeVariantStyles = () => {
    switch (size) {
      case 'small':
        return {
          '--height': '32px',
          '--padding': '12px',
          '--font-size': '14px',
          '--border-radius': '4px',
          '--svg-size': '14px',
          '--svg-margin': '4px',
        }
      case 'regular':
        return {
          '--height': '37px',
          '--padding': '16px',
          '--font-size': '14px',
          '--border-radius': '4px',
          '--svg-size': '16px',
          '--svg-margin': '4px',
        }
      case 'large':
        return {
          '--height': '48px',
          '--padding': '24px',
          '--font-size': '16px',
          '--border-radius': '6px',
          '--svg-size': '20px',
          '--svg-margin': '6px',
        }
      case 'huge':
        return {
          '--height': '56px',
          '--padding': '24px',
          '--font-size': '18px',
          '--border-radius': '6px',
          '--svg-size': '24px',
          '--svg-margin': '8px',
        }
      default:
        return {
          '--height': '37px',
          '--padding': '16px',
          '--font-size': '14px',
          '--border-radius': '4px',
          '--svg-size': '16px',
          '--svg-margin': '4px',
        }
    }
  }

  const renderButtonContent = () => (
    <>
      {loading && (
        <Loader
          active
          size="small"
          color={variant === 'primary' ? undefined : '#e6e8eb'}
        />
      )}
      {leftIcon}
      <label>{content}</label>
      {rightIcon}
      {!isNil(count) && (
        <NumberBubble
          className={countStyle === 'notify' ? 'notify' : undefined}>
          {count}
        </NumberBubble>
      )}
    </>
  )

  return (
    <ButtonWrapper className={wrapperClassNames.join(' ')}>
      <Popup
        style={{ width: '100%' }}
        inverted
        disabled={!(disabled && disabledReason)}
        position={disabledReasonPopupPosition}
        content={
          <span style={{ whiteSpace: 'pre-line', wordBreak: 'break-word' }}>
            {disabledReason}
          </span>
        }
        trigger={
          <div
            style={{
              display: 'flex',
            }}>
            {linkTo ? (
              <StyledButton
                as={Link}
                to={linkTo}
                state={linkState}
                style={
                  {
                    ...style,
                    ...getSizeVariantStyles(),
                  } as React.CSSProperties
                }
                type={type}
                className={classNames.join(' ')}
                iconColor={iconColor}
                {...rest}>
                {renderButtonContent()}
              </StyledButton>
            ) : (
              <StyledButton
                style={
                  {
                    ...style,
                    ...getSizeVariantStyles(),
                  } as React.CSSProperties
                }
                disabled={disabled || loading}
                onClick={(
                  e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
                ) => {
                  if (!onClick) {
                    return
                  }

                  onClick(e)
                }}
                type={type}
                className={classNames.join(' ')}
                iconColor={iconColor}
                form={form}
                {...rest}>
                {renderButtonContent()}
              </StyledButton>
            )}
          </div>
        }
      />
    </ButtonWrapper>
  )
}

Button.Add = (props: Omit<ButtonProps, 'icon'>) => {
  return <Button {...props} leftIcon={<AddIcon />} />
}

Button.Edit = (props: Omit<ButtonProps, 'icon'>) => {
  return <Button {...props} leftIcon={<EditIcon />} />
}

Button.Remove = (props: Omit<ButtonProps, 'icon'>) => {
  return <Button {...props} leftIcon={<RemoveIcon />} />
}

Button.Cancel = (props: Omit<ButtonProps, 'type'>) => {
  return <Button {...props} type="button" className="cancel" />
}

Button.Group = ({
  children,
  packTightly,
  vertical,
  className,
  style,
}: PropsWithChildren<ButtonGroupProps>) => {
  const classNames: string[] = className ? [className] : []

  if (packTightly) {
    classNames.push('packed-tightly')
  }

  if (vertical) {
    classNames.push('vertical')
  }

  return (
    <StyledButtonGroup className={classNames.join(' ')} style={style}>
      {children}
    </StyledButtonGroup>
  )
}

export default Button
